/**
 * Shared helpers for mapping AG Grid filterModel / sortModel to API payloads.
 */
import moment from 'moment';
import {
  formatFromDateForAPI,
  formatToDateForAPI,
  getDefaultDateRange,
} from '../../utils/datetime.utils';
import {
  DateRangeFilter,
  DateRangeFloatingFilter,
} from './DateRangeFilter';

export const formatAddressField = (value) => {
  if ([null, 'null', undefined, 'undefined', ''].includes(value)) {
    return '';
  }
  return value;
};

/**
 * Params for the shared From / To datetime filter popup.
 */
export const DATE_COLUMN_FILTER_PARAMS = {
  debounceMs: 400,
};

/** Labels stored by the API for the Consult Required column. */
export const CONSULT_STATUS_VALUES = ['Yes', 'No', 'Completed'];

export const consultStatusFilterParams = (t) => ({
  values: CONSULT_STATUS_VALUES,
  suppressSorting: true,
  buttons: ['reset'],
  valueFormatter: (params) => {
    const value = params && params.value;
    if (value === 'Yes') return t('Common.Yes');
    if (value === 'No') return t('Common.No');
    if (value === 'Completed') return t('Global.Consultation.Status.Completed');
    return value;
  },
});

/**
 * Override a column def to use the shared From / To datetime filter.
 */
export const withDateColumnFilter = (colDef = {}) => {
  if (!colDef || colDef._ilocalDateRangeFilter) return colDef;
  return {
    ...colDef,
    _ilocalDateRangeFilter: true,
    filter: DateRangeFilter,
    floatingFilterComponent: DateRangeFloatingFilter,
    filterParams: {
      ...DATE_COLUMN_FILTER_PARAMS,
      ...(colDef.filterParams || {}),
    },
    floatingFilter: true,
  };
};

const DATE_ID_RE =
  /(date|timestamp|dob|createdon|created_on|stockeddate|filldate|pickupdate|pickuped|enroll|expir|receipt|reciept|\bschedule\b|lastlogin|last_login|consultstart|consultend|paydate)/i;
const DATE_HEADER_RE = /\b(date|time|dob|timestamp)\b/i;
// "updated" contains "date", so updated_by / created_by labels must stay text filters.
const NOT_A_DATE_ID_RE = /(dea_schedule|_by(_|$))/i;

export const looksLikeDateColumn = (colDef) => {
  if (!colDef || colDef.filter === false) return false;
  if (colDef.colId === 'ilocal-selection' || colDef.checkboxSelection) {
    return false;
  }
  const id = `${colDef.colId || ''} ${colDef.field || ''}`;
  if (NOT_A_DATE_ID_RE.test(id)) return false;
  if (DATE_ID_RE.test(id)) return true;
  return DATE_HEADER_RE.test(String(colDef.headerName || ''));
};

const hasNonDateFilter = (col) => {
  if (!col || !col.filter || col.filter === false) return false;
  if (col._ilocalDateRangeFilter || col.filter === DateRangeFilter) return false;
  if (col.filter === 'agDateColumnFilter') return false;
  return true;
};

/**
 * Apply the shared From / To datetime filter to every date column.
 * Skips columns that already have a custom filter or filter: false.
 */
export const applyDateColumnFilters = (columnDefs) => {
  if (!Array.isArray(columnDefs)) return columnDefs;
  return columnDefs.map((col) => {
    if (!col || col.filter === false) return col;
    if (col._ilocalDateRangeFilter || col.filter === DateRangeFilter) {
      return col;
    }
    if (hasNonDateFilter(col)) return col;
    if (!looksLikeDateColumn(col)) return col;
    return withDateColumnFilter(col);
  });
};

export const isDateFilterModel = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.dateFrom || entry.dateTo) return true;
  if (entry.filterType === 'date' || entry.type === 'inRange') return true;
  if (entry.type === 'fromdate' || entry.type === 'todate') return true;
  return false;
};

const hasClockTime = (m) =>
  m.hours() !== 0 || m.minutes() !== 0 || m.seconds() !== 0;

const toApiDate = (value, endOfDay) => {
  if (value == null || value === '') return '';
  const m = moment(value);
  if (!m.isValid()) return '';
  if (!hasClockTime(m)) {
    return endOfDay ? formatToDateForAPI(m) : formatFromDateForAPI(m);
  }
  if (endOfDay && m.hours() === 23 && m.minutes() === 59 && m.seconds() === 0) {
    return m.clone().seconds(59).format('YYYY-MM-DD HH:mm:ss');
  }
  return m.format('YYYY-MM-DD HH:mm:ss');
};

const toGridDate = (value) => {
  if (value == null || value === '') return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  return m.format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Build AG Grid filterModel entry for a date-range column from FilterObj dates.
 */
export const buildDateRangeFilterModel = (colId, fromdate, todate) => {
  if (!colId) return {};
  const from = toGridDate(fromdate);
  const to = toGridDate(todate);
  if (!from && !to) return {};

  return {
    [colId]: {
      filterType: 'date',
      type: 'inRange',
      dateFrom: from || null,
      dateTo: to || null,
    },
  };
};

/**
 * Seed AG Grid filter UI from FilterObj.fromdate/todate so applied defaults are visible.
 * Prefer calling AFTER the first SSRM block has loaded. Safe to call multiple times.
 */
export const seedReportDateFilter = (api, filterBody, dateColId) => {
  if (!api || !filterBody || !dateColId) return false;
  if (!filterBody.fromdate && !filterBody.todate) return false;

  const existing =
    typeof api.getFilterModel === 'function' ? api.getFilterModel() || {} : {};
  if (existing[dateColId] && filterBody.__dateFilterBound) {
    return false;
  }

  const dateModel = buildDateRangeFilterModel(
    dateColId,
    filterBody.fromdate,
    filterBody.todate
  );
  if (!Object.keys(dateModel).length) return false;

  if (typeof api.setFilterModel === 'function') {
    // Signal grid to skip column-pinning during this programmatic seed.
    api.__ilocalSeedingFilter = true;
    api.setFilterModel({ ...existing, ...dateModel });
    filterBody.__dateFilterBound = true;
    setTimeout(() => {
      api.__ilocalSeedingFilter = false;
    }, 250);
    return true;
  }
  return false;
};

/**
 * Attach SSRM datasource. Prefer: seed date filter UI first (no datasource yet → no
 * fetch), then attach datasource so the first getRows already matches the visible filter.
 * FilterObj should already carry fromdate/todate (module/constructor defaults).
 */
export const attachReportServerSideDatasource = (
  params,
  datasource,
  filterBody,
  dateColId
) => {
  if (!params || !params.api) return;

  let attached = false;
  const attach = () => {
    if (attached) return;
    attached = true;
    // Seed UI before datasource so we don't stack an extra filter refresh on load.
    if (dateColId && filterBody) {
      seedReportDateFilter(params.api, filterBody, dateColId);
    }
    if (typeof params.api.setServerSideDatasource === 'function') {
      params.api.setServerSideDatasource(datasource);
    }
  };

  const hasDates = !!(filterBody && (filterBody.fromdate || filterBody.todate));
  if (!dateColId || hasDates) {
    attach();
    return;
  }

  const tryAttach = () => {
    if (filterBody && (filterBody.fromdate || filterBody.todate)) {
      attach();
      return true;
    }
    return false;
  };
  [0, 50, 100, 200, 400].forEach((ms) => setTimeout(tryAttach, ms));
  setTimeout(attach, 600);
};

const readDateCondition = (condition) => {
  if (!condition) return { type: null, date: '' };
  const type = condition.type || null;
  const raw = condition.dateFrom || condition.dateTo || null;
  return { type, date: raw };
};

/** Restore last-7-days defaults — never allow unbounded report date queries. */
export const restoreDefaultReportDates = (body) => {
  if (!body) return body;
  const defaults = getDefaultDateRange();
  body.fromdate = formatFromDateForAPI(defaults.fromDate);
  body.todate = formatToDateForAPI(defaults.toDate);
  return body;
};

/**
 * Map AG Grid date filterModel → body.fromdate / body.todate.
 * Clearing the date filter clears API dates too (no date constraint).
 */
export const applyDateRangeFromFilterModel = (
  body,
  filterModel,
  dateColIds = []
) => {
  if (!body || !dateColIds || !dateColIds.length) return body;

  body.__dateFilterColId = dateColIds[0];

  const model = filterModel || {};
  let foundCol = null;
  for (let i = 0; i < dateColIds.length; i += 1) {
    if (model[dateColIds[i]]) {
      foundCol = dateColIds[i];
      break;
    }
  }

  if (!foundCol) {
    if (body.__dateFilterBound) {
      body.fromdate = '';
      body.todate = '';
      body.__dateFilterBound = false;
    }
    return body;
  }

  body.__dateFilterBound = true;
  const dateValue = model[foundCol];
  let fromdate = '';
  let todate = '';

  const conditions = [];
  if (dateValue.conditions && Array.isArray(dateValue.conditions)) {
    dateValue.conditions.forEach((c) => conditions.push(c));
  } else if (dateValue.condition1 || dateValue.condition2) {
    if (dateValue.condition1) conditions.push(dateValue.condition1);
    if (dateValue.condition2) conditions.push(dateValue.condition2);
  } else {
    conditions.push(dateValue);
  }

  conditions.forEach((c) => {
    const { type, date } = readDateCondition(c);
    if (type === 'fromdate') fromdate = toApiDate(date, false);
    else if (type === 'todate') todate = toApiDate(date, true);
    else if (type === 'equals' || type === 'dateEquals') {
      fromdate = toApiDate(date, false);
      todate = toApiDate(date, true);
    } else if (type === 'greaterThan' || type === 'greaterThanOrEqual') {
      fromdate = toApiDate(date, false);
    } else if (type === 'lessThan' || type === 'lessThanOrEqual') {
      todate = toApiDate(date, type === 'lessThan');
    } else if (type === 'inRange') {
      fromdate = toApiDate(c.dateFrom || date, false);
      todate = toApiDate(c.dateTo, true);
    } else if (!fromdate && date) {
      fromdate = toApiDate(date, false);
      todate = toApiDate(date, true);
    }
  });

  // Empty date inputs on an active model also count as "cleared".
  if (!fromdate && !todate) {
    body.fromdate = '';
    body.todate = '';
    body.__dateFilterBound = false;
    return body;
  }

  body.fromdate = fromdate;
  body.todate = todate;
  return body;
};

const collectSetValues = (entry) => {
  if (!entry || !Array.isArray(entry.values)) return [];
  return entry.values
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter((v) => v !== '');
};

/**
 * Flatten AG Grid text / set / multi filter models into a searchObject entry.
 * Text → { filter }. Set → { filterType: 'set', values }. Both → both fields.
 */
export const normalizeColumnFilter = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const parts = [];
  if (entry.filterType === 'multi' && Array.isArray(entry.filterModels)) {
    entry.filterModels.forEach((m) => {
      if (m) parts.push(m);
    });
  } else {
    parts.push(entry);
  }

  const datePart = parts.find((part) => isDateFilterModel(part));
  if (datePart && (datePart.dateFrom || datePart.dateTo)) {
    return {
      filterType: 'date',
      type: datePart.type || 'inRange',
      dateFrom: datePart.dateFrom || null,
      dateTo: datePart.dateTo || null,
    };
  }

  let textFilter = '';
  let textModel = null;
  const values = [];
  parts.forEach((part) => {
    if (!part) return;
    if (Array.isArray(part.conditions)) {
      part.conditions.forEach((c) => {
        const nested = normalizeColumnFilter(c);
        if (nested && nested.filter && !textFilter) textFilter = nested.filter;
        if (nested && nested.values) nested.values.forEach((v) => values.push(v));
      });
      return;
    }
    const setValues = collectSetValues(part);
    if (setValues.length) {
      setValues.forEach((v) => {
        if (!values.includes(v)) values.push(v);
      });
    }
    if (part.filter != null && String(part.filter).trim() !== '') {
      textFilter = String(part.filter).trim();
      textModel = part;
    }
  });

  if (!textFilter && !values.length) return null;
  const out = textModel ? { ...textModel, filter: textFilter } : {};
  if (textFilter) out.filter = textFilter;
  if (values.length) {
    out.values = values;
    if (!out.filterType) out.filterType = textFilter ? 'text' : 'set';
  }
  return out;
};

export const serializeFilterModelEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const parts = [
    entry.filterType || '',
    entry.type || '',
    entry.filter || '',
    entry.dateFrom || '',
    entry.dateTo || '',
  ];
  if (Array.isArray(entry.values)) parts.push(entry.values.join(','));
  if (Array.isArray(entry.filterModels)) {
    parts.push(entry.filterModels.map(serializeFilterModelEntry).join('+'));
  }
  if (Array.isArray(entry.conditions)) {
    parts.push(entry.conditions.map(serializeFilterModelEntry).join('+'));
  }
  if (entry.condition1) parts.push(serializeFilterModelEntry(entry.condition1));
  if (entry.condition2) parts.push(serializeFilterModelEntry(entry.condition2));
  return parts.join(':');
};

/**
 * Fold AG Grid filterModel into a single searchtext string for APIs
 * that only support free-text search (uses first non-empty filter value).
 */
export const foldFiltersToSearchText = (filterModel, { excludeColIds = [] } = {}) => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return '';
  }
  const exclude = new Set(excludeColIds || []);
  const values = Object.keys(filterModel)
    .filter((colId) => !exclude.has(colId))
    .map((colId) => {
      const normalized = normalizeColumnFilter(filterModel[colId]);
      if (!normalized) return '';
      if (normalized.filter) return normalized.filter;
      if (Array.isArray(normalized.values) && normalized.values.length) {
        return normalized.values[0];
      }
      return '';
    })
    .filter((v) => v != null && String(v).trim() !== '');
  return values.length > 0 ? String(values[0]).trim() : '';
};

/**
 * Standard report applyFilterModel: date range cols → fromdate/todate,
 * remaining text / set filters → searchtext / searchObject.
 */
export const applyReportFilterModel = (
  body,
  filterModel,
  { dateColIds = [] } = {}
) => {
  applyDateRangeFromFilterModel(body, filterModel, dateColIds);

  // Primary dateColIds → fromdate/todate. Other date columns and text/set
  // filters go to searchObject so every date column can filter independently.
  const extraFilters = {};
  const model = filterModel || {};
  const dateSet = new Set(dateColIds || []);
  Object.keys(model).forEach((colId) => {
    if (dateSet.has(colId)) return;
    const normalized = normalizeColumnFilter(model[colId]);
    if (normalized) {
      extraFilters[colId] = normalized;
    }
  });
  body.searchObject = Object.keys(extraFilters).length ? extraFilters : null;

  // Column filters must not also run as a global OR across every field.
  body.searchtext = body.searchObject
    ? ''
    : foldFiltersToSearchText(filterModel, { excludeColIds: dateColIds });
  return body;
};

/**
 * Maps AG Grid filterModel keys to API searchObject keys via fieldMap.
 */
export const buildSearchObject = (filterModel, fieldMap = {}) => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return '';
  }

  const searchObject = {};
  Object.keys(filterModel).forEach((colId) => {
    const filter = filterModel[colId];
    const mappedKey = fieldMap[colId] !== undefined ? fieldMap[colId] : colId;
    if (mappedKey && filter) {
      searchObject[mappedKey] = filter;
    }
  });

  return Object.keys(searchObject).length > 0 ? searchObject : '';
};

/**
 * Applies sortModel to a mutable filter/sort body using a sort map.
 * Falls back to the AG Grid colId when the column is not in sortMap.
 */
export const applySortModel = (body, sortModel, sortMap, tableAliases = {}) => {
  if (!sortModel || sortModel.length === 0) {
    return body;
  }
  const first = sortModel[0];
  const mapping = sortMap && sortMap[first.colId];
  let tableName = mapping && mapping.tableName != null ? mapping.tableName : '';
  if (tableName && tableAliases[tableName]) {
    tableName = tableAliases[tableName];
  }
  body.tableName = tableName;
  body.sortColumn = (mapping && mapping.sortColumn) || first.colId;
  body.sortOrder = first.sort;
  return body;
};

export const buildManageBoxesSearchObject = (filterModel) =>
  buildSearchObject(filterModel, {
    name: 'name',
    description: 'description',
    'type.name': 'type',
    type: 'type',
    'address.city': 'city',
    city: 'city',
    'address.state': 'state',
    state: 'state',
    'address.zip': 'zip',
    zip: 'zip',
    'account.name': 'username',
    parentAccount: 'username',
  });

export const buildManageAccountsSearchObject = (filterModel) =>
  buildSearchObject(filterModel, {
    name: 'name',
    'type.name': 'type',
    type: 'type',
    'parent.name': 'parent',
    parent_account: 'parent',
    'contact.address.city': 'city',
    city: 'city',
    'contact.address.state': 'state',
    state: 'state',
    'contact.address.zip': 'zip',
    zip: 'zip',
  });



// WEBPACK FOOTER //
// ./src/components/grid/filterUtils.js